import * as React from 'react';

import { useRouter } from 'expo-router';
import { StyleSheet } from 'react-native';

import { Box, Typography } from 'components/ui';
import { useRoute } from 'hooks/navigation/use-route';
import { routes } from 'navigation/routing';
import { t, tRich } from 'state/intl/translations';
import type { TranslationKey } from 'state/intl/types';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLoyaltyClaimPointsContext } from 'state/loyalty-claim-points';
import { blockContentToPlainText } from 'utils/sanity';

import {
  AlreadyEarnedCrownsLink,
  EarnedPointsText,
  EarnedPointsWrapper,
  StyledLoyaltyPointsIcon,
} from './loyalty-claim-points.styled';
import LoyaltyClaimPointsView from './loyalty-claim-points.view';
import SuccessAnimation from './success-animation';
import { isClaimPointsHref } from './utils';

const LoyaltyClaimPointsAccepted = () => {
  const { data } = useLoyaltyClaimPointsContext();
  const { navigate, replace } = useRouter();
  const { params } = useRoute<{
    alreadyEarned?: string;
    earnedPoints?: string | number;
    transactionId?: string;
  }>();
  const enableLoyaltyProfile = Boolean(useFlag(LaunchDarklyFlag.ENABLE_LOYALTY_PROFILE));

  const handleOnPress = (path?: string | null) => {
    const destination = path || '/';
    if (isClaimPointsHref(destination)) {
      replace(destination);
      return;
    }
    navigate(destination);
  };

  const content = data?.claimPointsSuccessfulSubmissionScreenContent;
  const alreadyEarnedContent = data?.claimPointsAlreadyEarnedContent;
  const alreadyEarned = params.alreadyEarned === '1' || params.alreadyEarned === 'true';
  const earnedPoints = Number(params.earnedPoints) || 0;
  const transactionId = typeof params.transactionId === 'string' ? params.transactionId : undefined;
  const historyHref = `${enableLoyaltyProfile ? routes.history : routes.rewardsHistory}${
    transactionId ? `?transactionId=${encodeURIComponent(transactionId)}` : ''
  }`;
  const bodyBefore = blockContentToPlainText(
    alreadyEarnedContent?.bodyTextBefore?.localeRaw ?? undefined
  );
  const crownsWord = blockContentToPlainText(
    alreadyEarnedContent?.crownsLinkText?.localeRaw ?? undefined
  );
  const bodyAfter = blockContentToPlainText(
    alreadyEarnedContent?.bodyTextAfter?.localeRaw ?? undefined
  );
  const hasSanityBody = Boolean(bodyBefore && crownsWord && bodyAfter);
  const crownsPhrase = hasSanityBody
    ? [String(earnedPoints), crownsWord].filter(Boolean).join(' ')
    : t('alreadyEarnedCrownsLinkText', {
        defaultValue: '{points} Crowns',
        points: earnedPoints,
      });
  const crownsNode = transactionId ? (
    <AlreadyEarnedCrownsLink to={historyHref}>{crownsPhrase}</AlreadyEarnedCrownsLink>
  ) : (
    crownsPhrase
  );
  const alreadyEarnedBody = hasSanityBody ? (
    transactionId ? (
      <>
        {bodyBefore}
        {bodyBefore ? ' ' : ''}
        {crownsNode}
        {bodyAfter ? ` ${bodyAfter}` : ''}
      </>
    ) : (
      [bodyBefore, crownsPhrase, bodyAfter].filter(Boolean).join(' ')
    )
  ) : (
    tRich(
      'alreadyEarnedCrownsBody' as TranslationKey,
      { crowns: crownsNode },
      { defaultValue: 'We already added {crowns} from this order to your account.' }
    )
  );
  const alreadyEarnedTitle =
    alreadyEarnedContent?.screenTitle?.locale?.trim() ||
    t('alreadyEarnedCrownsTitle', {
      defaultValue: 'You already earned your Crowns!',
    });

  return (
    <>
      <Box style={styles.successAnimationContainer}>
        <SuccessAnimation />
      </Box>
      <LoyaltyClaimPointsView
        content={content}
        onPress={handleOnPress}
        title={alreadyEarned ? alreadyEarnedTitle : undefined}
        body={
          alreadyEarned ? (
            <Typography textAlign="center">{alreadyEarnedBody}</Typography>
          ) : undefined
        }
        bottomButtonConfig={{
          asLink: true,
          replace: isClaimPointsHref(content?.bottomButtonUrl?.locale),
        }}
      >
        {!!earnedPoints && !alreadyEarned && (
          <EarnedPointsWrapper>
            <Typography>{t('earned')}</Typography>
            <StyledLoyaltyPointsIcon />
            <EarnedPointsText>{earnedPoints}</EarnedPointsText>
          </EarnedPointsWrapper>
        )}
      </LoyaltyClaimPointsView>
    </>
  );
};

export default LoyaltyClaimPointsAccepted;

const styles = StyleSheet.create({
  successAnimationContainer: {
    position: 'absolute',
  },
});
